import React from 'react';
import { createPath, To, UNSAFE_NavigationContext, useLocation } from 'react-router-dom';

import { setHistoryTraversalBlocker } from 'utils/historyTraversalGuard';

export const UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes to this question. Discard them?';

// Resolved against the router's location (not window.location), so it also works in memory routers.
const toUrl = (to: To, from: string) => new URL(typeof to === 'string' ? to : createPath(to), from);

/**
 * Protects an unsaved draft from navigation the page's own handlers don't see.
 *
 * The app uses a component router (not a data router), so `useBlocker` is unavailable. Instead this:
 * - asks before reload/close and cross-document navigation (`beforeunload`);
 * - asks before any in-app navigation (links, header/sidebar buttons, `navigate()`) by wrapping the router's
 *   push/replace. `allowNavigation` lets the page's own URL updates through; those ask for themselves;
 * - asks on browser Back/Forward through `utils/historyTraversalGuard`, which sees the traversal before the
 *   router and restores the original history position when the author stays.
 */
export const useUnsavedChangesGuard = (
  dirty: boolean,
  {
    message = UNSAVED_CHANGES_MESSAGE,
    allowNavigation,
  }: { message?: string; allowNavigation?: (destination: URL, current: URL) => boolean } = {},
) => {
  const location = useLocation();
  const { navigator } = React.useContext(UNSAFE_NavigationContext);
  const allowNavigationRef = React.useRef(allowNavigation);
  allowNavigationRef.current = allowNavigation;
  const currentHrefRef = React.useRef('');
  currentHrefRef.current = new URL(createPath(location), window.location.origin).href;

  React.useEffect(() => {
    if (!dirty) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Legacy browsers require a return value to show the prompt.
      event.returnValue = message;
      return message;
    };

    const { push, replace } = navigator;
    const guarded = (navigate: typeof push): typeof push => (to, state, options) => {
      const current = new URL(currentHrefRef.current);
      if (allowNavigationRef.current?.(toUrl(to, current.href), current) || window.confirm(message)) {
        navigate(to, state, options);
      }
    };
    navigator.push = guarded(push);
    navigator.replace = guarded(replace);

    window.addEventListener('beforeunload', handleBeforeUnload);
    const removeTraversalBlocker = setHistoryTraversalBlocker(() => window.confirm(message));
    return () => {
      navigator.push = push;
      navigator.replace = replace;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      removeTraversalBlocker();
    };
  }, [dirty, message, navigator]);
};

export default useUnsavedChangesGuard;
